import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  Notice,
  Panel,
  SearchBox,
  Spinner,
  Table,
  TableWrap,
  Toolbar,
} from 'iboon-ui';

import { api, type Asset, type Person } from '../api';

/**
 * Who holds what.
 *
 * Nobody is created or edited here. iBoon Identity owns who exists and Keka
 * owns their employment; this reads the directory and keeps only enough to
 * name a holder. A create-and-edit screen for staff inside an asset register
 * is how two systems end up disagreeing about whether somebody still works
 * here -- and the register would lose that argument.
 *
 * Somebody who leaves is marked inactive rather than deleted, so they stop
 * being offered as a holder while the history of what they had survives.
 */
export function People() {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [looking, setLooking] = useState<Person | null>(null);

  const load = useCallback(async (q: string) => {
    const result = await api.get<{ people: Person[] }>(
      `/api/people${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`,
    );
    setPeople(result.people);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      load(query).catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : 'Could not load the directory.'),
      );
    }, 200);
    return () => clearTimeout(timer);
  }, [query, load]);

  /*
   * Refreshing is a trip through identity, not a request to our own server.
   *
   * Reading the directory needs this administrator's identity token, and the
   * only moment this application holds one is the authorization-code exchange
   * -- it is deliberately never stored. So the button signs in again, which
   * with an existing identity session is a redirect out and straight back,
   * landing here with the directory already refreshed.
   */
  function refresh() {
    setSyncing(true);
    window.location.href = '/api/auth/login?next=/people';
  }

  if (error) {
    return (
      <Notice tone="danger" title="Could not load the directory">
        {error}
      </Notice>
    );
  }

  return (
    <>
      <Toolbar>
        <SearchBox value={query} onValueChange={setQuery} placeholder="Name or email…" />
        <Button variant="primary" disabled={syncing} onClick={refresh}>
          {syncing ? 'Refreshing…' : 'Refresh from identity'}
        </Button>
      </Toolbar>

      {!people ? (
        <Spinner />
      ) : people.length === 0 ? (
        <EmptyState
          title={query ? 'Nobody matches' : 'The directory is empty'}
          description={
            query
              ? 'No one here matches that.'
              : 'Refresh from identity to bring in everybody who has an iBoon account.'
          }
        />
      ) : (
        <Card padded={false}>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Who</th>
                  <th className="num">Holding</th>
                  <th className="num">Machines</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {people.map((person) => (
                  <tr key={person.id}>
                    <td className="tcell-name">
                      {person.name}
                      <div className="sub">{person.email}</div>
                    </td>
                    <td className="num">{person.assetCount}</td>
                    {/* Kept apart from the count beside it. A registered
                        machine is one that reported itself; an asset is one
                        somebody recorded buying. Adding them together reads as
                        "holds two things" when the register only knows one. */}
                    <td className="num">{person.deviceCount || <span className="muted">—</span>}</td>
                    <td>
                      <div className="row-actions">
                        {!person.active && <Badge tone="neutral">left</Badge>}
                        <Button variant="ghost" onClick={() => setLooking(person)}>
                          What they have
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}

      {looking && <Holding person={looking} onClose={() => setLooking(null)} />}
    </>
  );
}

/**
 * Everything one person is holding, assets and stock together.
 *
 * This is the offboarding list. Somebody leaves, and the question is what has
 * to come back -- which is useless if the answer only covers laptops and
 * forgets the four cables and the dock.
 */
type HeldDevice = {
  id: string;
  hostname: string;
  platform: string;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  assetId: string | null;
  assetName: string | null;
};

function Holding({ person, onClose }: { person: Person; onClose: () => void }) {
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [stock, setStock] = useState<Array<{ id: string; name: string; categoryName: string; holding: number }> | null>(
    null,
  );
  const [devices, setDevices] = useState<HeldDevice[] | null>(null);

  useEffect(() => {
    api.get<{ assets: Asset[] }>(`/api/assets/held-by/${person.id}`).then((r) => setAssets(r.assets));
    api
      .get<{ items: Array<{ id: string; name: string; categoryName: string; holding: number }> }>(
        `/api/stock/held-by/${person.id}`,
      )
      .then((r) => setStock(r.items));
    // Devices too, because "Nothing at all" while a machine is plainly
    // registered to somebody is the answer that sent somebody to ask whether
    // registration had worked.
    api
      .get<{ devices: HeldDevice[] }>('/api/devices')
      .then((r) => setDevices(r.devices.filter((d) => (d as unknown as { personId: string }).personId === person.id)))
      .catch(() => setDevices([]));
  }, [person.id]);

  const nothing = assets?.length === 0 && stock?.length === 0 && devices?.length === 0;

  return (
    <Modal open size="md" title={`${person.name} has…`} onClose={onClose}>
      {!assets || !stock || !devices ? (
        <Spinner />
      ) : nothing ? (
        <p className="muted">Nothing at all.</p>
      ) : (
        <>
          {assets.length > 0 && (
            <Panel title="Assets">
              {assets.map((asset) => (
                <div key={asset.id} className="line">
                  <span>
                    {asset.name}
                    <div className="sub">
                      {[asset.assetTag, asset.serialNumber].filter(Boolean).join(' · ') ||
                        asset.categoryName}
                    </div>
                  </span>
                  <span className="sub">{asset.categoryName}</span>
                </div>
              ))}
            </Panel>
          )}

          {devices.length > 0 && (
            <Panel title="Machines registered to them">
              {devices.map((device) => (
                <div key={device.id} className="line">
                  <span>
                    {device.hostname}
                    <div className="sub">
                      {[device.manufacturer, device.model, device.serialNumber]
                        .filter(Boolean)
                        .join(' · ') || device.platform}
                    </div>
                  </span>
                  <span className="sub">
                    {device.assetName ?? 'not in the register as an asset'}
                  </span>
                </div>
              ))}
            </Panel>
          )}

          {stock.length > 0 && (
            <Panel title="Stock">
              {stock.map((item) => (
                <div key={item.id} className="line">
                  <span>
                    {item.name}
                    <div className="sub">{item.categoryName}</div>
                  </span>
                  <span className="num">{item.holding}</span>
                </div>
              ))}
            </Panel>
          )}
        </>
      )}
    </Modal>
  );
}
